import { useEffect, useCallback } from 'react';
import { create } from 'zustand';
import {
  clientAuthorizedGet,
  clientAuthorizedPost,
  clientAuthorizedPatch,
  clientAuthorizedDelete,
} from 'utils/fetchUtils';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { API_ROUTES } from 'utils/constants';
import {
  cacheCategories,
  getCachedCategories,
} from 'utils/categoriesIndexedDBUtils';
import { syncPendingCategories } from 'utils/categorySyncService';
import Category from 'types/category';

let categoryChannelRef: BroadcastChannel | null = null;
let isRemoteCategoryUpdate = false;
let fetchCategoriesRef: ((isReload?: boolean) => Promise<void>) | null = null;
/** Tracks which company we've completed initial load for. Skip re-fetch if already loaded. */
let initializedCompanyId: string | null = null;

interface CategoryStore {
  categories: { [key: string]: Category };
  loading: boolean;
  reloading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  updateCategories: (categories: { [key: string]: Category }) => void;
  addCategory: (newCategory: Category) => void;
  removeCategory: (categoryId: string) => void;
  setCategories: (categories: { [key: string]: Category }) => void;
  reset: () => void;
}

export const useCategoryStore = create<CategoryStore>((set) => ({
  categories: {},
  loading: true,
  reloading: false,
  error: null,

  reload: async () => {
    if (fetchCategoriesRef) {
      await fetchCategoriesRef(true);
    }
  },

  updateCategories: (updatedCategories) => {
    set({ categories: updatedCategories });
    void cacheCategories(updatedCategories);
    if (!isRemoteCategoryUpdate) {
      categoryChannelRef?.postMessage(updatedCategories);
    }
    isRemoteCategoryUpdate = false;
  },

  addCategory: (newCategory) => {
    set((state) => {
      const updated = { ...state.categories, [newCategory.id]: newCategory };
      void cacheCategories(updated);
      categoryChannelRef?.postMessage(updated);
      return { categories: updated };
    });
  },

  removeCategory: (categoryId) => {
    set((state) => {
      const updated = { ...state.categories };
      delete updated[categoryId];
      void cacheCategories(updated);
      categoryChannelRef?.postMessage(updated);
      return { categories: updated };
    });
  },

  setCategories: (categories) => set({ categories }),

  reset: () => set({ categories: {}, loading: true, error: null }),
}));

export function useCategoryInit(): void {
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const isCompanyInitialized = useActiveCompanyStore((s) => s.isInitialized);
  const setCategories = useCategoryStore((s) => s.setCategories);

  const fetchCategories = useCallback(
    async (isReload = false) => {
      const companyId = useActiveCompanyStore.getState().activeCompanyId;
      if (!isCompanyInitialized || !companyId) return;

      // Idempotent: skip initial fetch if already loaded for this company
      if (!isReload && initializedCompanyId === companyId) return;

      useCategoryStore.setState(
        isReload ? { reloading: true } : { loading: true },
      );
      useCategoryStore.setState({ error: null });

      try {
        const fetchedCategories = await clientAuthorizedGet<{
          [key: string]: Category;
        }>(API_ROUTES.CATEGORY, undefined, companyId);

        initializedCompanyId = companyId;
        useCategoryStore.setState({ categories: fetchedCategories });
        await cacheCategories(fetchedCategories);
        categoryChannelRef?.postMessage(fetchedCategories);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to load categories. Please check your connection.';
        useCategoryStore.setState({ error: errorMessage });
      } finally {
        useCategoryStore.setState(
          isReload ? { reloading: false } : { loading: false },
        );
      }
    },
    [isCompanyInitialized],
  );

  fetchCategoriesRef = fetchCategories;

  useEffect(() => {
    const channel = new BroadcastChannel('categories');
    categoryChannelRef = channel;

    channel.onmessage = (event: MessageEvent<{ [key: string]: Category }>) => {
      isRemoteCategoryUpdate = true;
      setCategories(event.data);
      void cacheCategories(event.data);
    };

    return () => {
      channel.close();
      categoryChannelRef = null;
    };
  }, [setCategories]);

  useEffect(() => {
    initializedCompanyId = null;
    useCategoryStore.setState({ loading: true, categories: {}, error: null });
  }, [activeCompanyId]);

  useEffect(() => {
    const loadCache = async () => {
      if (!isCompanyInitialized) return;
      try {
        const cached = await getCachedCategories();
        if (Object.keys(cached).length > 0) {
          useCategoryStore.setState({ categories: cached, loading: false });
        }
      } catch (err) {
        console.warn('Failed to load cached categories', err);
      }
    };

    void loadCache();
  }, [isCompanyInitialized]);

  useEffect(() => {
    if (isCompanyInitialized) {
      void fetchCategories();
    }
  }, [isCompanyInitialized, fetchCategories]);

  const syncPending = useCallback(async () => {
    if (!navigator.onLine || !isCompanyInitialized) return;

    const companyId = useActiveCompanyStore.getState().activeCompanyId;
    if (!companyId) return;

    try {
      const result = await syncPendingCategories({
        post: (url, body) =>
          clientAuthorizedPost(url, body as Record<string, unknown>, companyId),
        patch: (url, body) =>
          clientAuthorizedPatch(
            url,
            body as Record<string, unknown>,
            companyId,
          ),
        delete: (url) => clientAuthorizedDelete(url, undefined, companyId),
      });

      if (result.synced > 0 && fetchCategoriesRef) {
        await fetchCategoriesRef(true);
      }
    } catch (err) {
      console.warn('Failed to sync pending categories', err);
    }
  }, [isCompanyInitialized]);

  useEffect(() => {
    void syncPending();
    const handleOnline = () => {
      void syncPending();
    };
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [syncPending]);
}
